import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef
} from 'react';
import classNames from 'classnames';
import { Button } from 'antd';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as BackSvg } from '@assets/icons/ShapesDrawingTools/back.svg';
import { ReactComponent as ForwardSvg } from '@assets/icons/ShapesDrawingTools/forward.svg';
import { useOutletContext } from 'react-router-dom';
import {
  getDocumentHighlightViewportPosition,
  getFloatCanvasAnnotations
} from '@pages/MediaViewer/components/FloatCommentItem/floatCommentHelpers';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import CommentEditor from '@pages/MediaViewer/Comments/components/CommentEditor';
import type { SubmitEditorValues } from '@pages/MediaViewer/Comments/components/CommentEditor/CommentEditor';
import {
  AssetItemDto,
  AssetVersionItemDto,
  MemberDto,
  TaskBoardItemDto
} from '@api/Api';
import { createNewComment } from '@redux/actions/mediaViewerAction';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useAuth } from '@hooks/useAuth';
import {
  setAssetListNeedRefresh,
  setListsNeedRefreshInCampaign
} from '@redux/reducers/tasks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useCreateTask } from '@hooks/tasks';
import { useFetch } from '@hooks/useFetch';
import {
  assetStateSelector,
  boardsSelector,
  campaignStateSelector
} from '@redux/selectors/tasks';
import {
  fetchAssetAssignees,
  fetchCampaignTaskBoards
} from '@redux/actions/tasks';
import { FabricContext } from '@context/FabricContext';
import useHasAnnotations from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useHasAnnotations';
import { useMVCommentAttachUppy } from '@hooks/useMVCommentAttachUppy';
import { MediaViewerOutletContextState } from '@layouts/types';
import CommentTask from '@pages/MediaViewer/Comments/components/CommentTask';
import {
  buildCreateCommentPayload,
  createTurnCommentIntoTask,
  mapUppyFilesToAttachments
} from '@pages/MediaViewer/helpers/comment';
import { useCommentMentions } from '@pages/MediaViewer/hooks/useComment';
import getObjectViewportPosition, {
  choosePosition,
  constrainPositionToWindow,
  getObjectViewportRect
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/getObjectViewportPosition';
import { useResponsive } from '@hooks/useResponsive';
import useCanvasHistory from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useCanvasHistory';

import './FloatCommentEditor.less';

type FloatCommentEditorProps = {
  selectedVersion: AssetVersionItemDto;
  asset: AssetItemDto;
  canCreateTask: boolean;
  commentMentions: boolean;
};

function FloatCommentEditor({
  canCreateTask,
  commentMentions,
  asset,
  selectedVersion
}: FloatCommentEditorProps) {
  const { isMdWidth, isSmWidth } = useResponsive();
  const { addNewComment, getPotentialMentions, getPotentialGroupMentions } =
    useOutletContext<MediaViewerOutletContextState>();
  const { clearCanvas, canvas } = useContext(FabricContext);
  const { user } = useAuth();
  const dispatch = useTypedDispatch();
  const createTask = useCreateTask();
  const hasAnnotations = useHasAnnotations();
  const canViewTasks = useAssetMVPermissions('listTasks');
  const { campaignId, workspaceId, id: assetId } = asset;
  const uppy = useMVCommentAttachUppy(assetId);
  const {
    activePageNumber,
    playerTime,
    selectedCommentId,
    setDrawToolsShape,
    selectComment,
    setCanvasMode,
    setDocumentHighlight,
    canvasMode,
    documentHighlight,
    shapesDrawingTools,
    setIsCommentFieldFocused
  } = useAssetViewerState();
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const shapeRef = useRef(shapesDrawingTools.shape);
  const isShapeDrawing = shapesDrawingTools.shape !== 'cursor';
  const [isShowTask, setIsShowTask] = useState<boolean>(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(true);
  const [selectedUser, setSelectedUser] = useState<MemberDto | null>(null);
  const [selectedBoard, setSelectedBoard] = useState<TaskBoardItemDto | null>(
    null
  );
  const assetType = selectedVersion?.metadata.type;
  const { history, mods, redoHistory, undoHistory } = useCanvasHistory({
    assetType
  });
  const canUndo = mods < history.length;
  const canRedo = mods > 0;
  const isAudio = assetType === 'video' || assetType === 'audio';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const checkboxValueType =
    (isDocument && 'pageNumber') || (isAudio && 'timeCode') || null;

  const potentialAssignees = useTypedSelector(
    (state) => assetStateSelector(state, { assetId }).potentialAssignees.list
  );
  const taskBoards = useTypedSelector((state) =>
    boardsSelector(state, { campaignId })
  ).filter((x) => x.id !== 'archived' && x.id !== 'no_assigned');

  useFetch({
    key: `campaign-task-board-${campaignId}`,
    disabled: user?.type !== 'internal' || !campaignId || !canViewTasks,
    selector: (state) =>
      campaignStateSelector(state, { campaignId }).boards.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchCampaignTaskBoards({
          campaignId,
          fetchType
        })
      );
    }
  });
  useEffect(() => {
    dispatch(setListsNeedRefreshInCampaign({ campaignId }));
  }, [campaignId, dispatch]);

  useFetch({
    key: `asset-assignees-${assetId}`,
    disabled: user?.type !== 'internal' || !assetId || !canViewTasks,
    selector: (state) =>
      assetStateSelector(state, { assetId }).potentialAssignees.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchAssetAssignees({
          workspaceId,
          assetId,
          fetchType
        })
      );
    }
  });

  useEffect(() => {
    dispatch(setAssetListNeedRefresh({ assetId }));
  }, [assetId, dispatch]);

  const onTurnCommentIntoTask = createTurnCommentIntoTask({
    asset,
    user,
    createTask,
    dispatch,
    selectedBoardId: selectedBoard?.id,
    selectedUser,
    isOpenTaskModal: !isSmWidth
  });

  const onCancel = () => {
    clearCanvas();
    setDrawToolsShape('cursor');
    setCanvasMode('view');
    selectComment(null);
    setDocumentHighlight(null);
    setIsMobileExpanded(false);
    setIsCommentFieldFocused(false);
  };

  const onAddComment = async ({ ...values }: SubmitEditorValues) => {
    const data = buildCreateCommentPayload({
      values,
      selectedVersionId: selectedVersion?.id || '',
      isCheckboxChecked,
      isAudioVideo: isAudio,
      isDocument,
      playerTime,
      activePageNumber,
      annotations: getAnnotations()
    });
    const comment = await addNewComment(data, '');
    const files = uppy.getFiles();
    if (files.length) {
      files.forEach((item) => {
        uppy.setFileMeta(item.id, {
          commentId: comment.id,
          readyForUpload: true
        });
      });
      await uppy.upload();
    }
    await createNewComment(
      {
        ...comment,
        attachments: mapUppyFilesToAttachments(files)
      },
      selectedVersion?.id || ''
    );
    onCancel();
    if (isShowTask) {
      onTurnCommentIntoTask(comment);
    }
  };

  const getAnnotations = (): string | null => {
    if (!canvas) return null;
    return getFloatCanvasAnnotations(canvas, {
      requireHasAnnotations: true,
      hasAnnotations
    });
  };

  const { potentialMentions, potentialGroupMentions, setSearchQuery } =
    useCommentMentions({
      assetVersionId: selectedVersion?.id,
      getPotentialMentions,
      getPotentialGroupMentions
    });

  const [hasActiveObject, setHasActiveObject] = useState(false);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const updateActiveObjectState = useCallback(() => {
    if (!canvas) return;
    if (canvasMode === 'view') return;
    const drawableObjects = canvas
      .getObjects()
      .filter((obj) => obj.type !== 'highlight' && obj.visible !== false);
    const activeObject = canvas.getActiveObject();
    const anchorObject = activeObject || drawableObjects[0];
    setHasActiveObject(!!anchorObject);
    if (anchorObject) {
      const initialPos = getObjectViewportPosition(canvas, anchorObject);
      const constrained = constrainPositionToWindow(initialPos);
      const objectRect = getObjectViewportRect(canvas, anchorObject);
      let pos: { left: number; top: number } | null = constrained;
      if (objectRect && constrained) {
        pos = choosePosition(canvas, objectRect, constrained) ?? constrained;
      }
      setPosition(pos);
    } else {
      setPosition(null);
    }
  }, [canvas, canvasMode]);

  useEffect(() => {
    if (!canvas) return;
    updateActiveObjectState();
    canvas.on('selection:created', updateActiveObjectState);
    canvas.on('selection:updated', updateActiveObjectState);
    canvas.on('selection:cleared', updateActiveObjectState);
    canvas.on('object:modified', updateActiveObjectState);
    canvas.on('object:deleted', updateActiveObjectState);
    canvas.on('object:history:add', updateActiveObjectState);
    canvas.on('object:history:remove', updateActiveObjectState);
    canvas.on('canvas:cleared', updateActiveObjectState);
    return () => {
      canvas.off('selection:created', updateActiveObjectState);
      canvas.off('selection:updated', updateActiveObjectState);
      canvas.off('selection:cleared', updateActiveObjectState);
      canvas.off('object:modified', updateActiveObjectState);
      canvas.off('object:deleted', updateActiveObjectState);
      canvas.off('object:history:add', updateActiveObjectState);
      canvas.off('object:history:remove', updateActiveObjectState);
      canvas.off('canvas:cleared', updateActiveObjectState);
    };
  }, [canvas, updateActiveObjectState]);

  const positionFromDocumentHighlight = useMemo(() => {
    if (isMdWidth) return null;
    if (canvasMode === 'view') return null;
    if (!canvas) return null;
    if (!documentHighlight?.text) return null;
    if (!documentHighlight?.rects?.length) return null;
    return getDocumentHighlightViewportPosition(
      canvas,
      documentHighlight.rects
    );
  }, [
    canvas,
    documentHighlight?.text,
    documentHighlight?.rects,
    canvasMode,
    isMdWidth
  ]);

  const isCreatingNewComment = selectedCommentId === 'new';

  const showForActiveObject =
    isCreatingNewComment && hasActiveObject && position;
  const showForDocumentHighlight =
    isCreatingNewComment &&
    !!documentHighlight?.text &&
    !!documentHighlight?.rects?.length &&
    positionFromDocumentHighlight;

  const shouldShow = isMdWidth
    ? isCreatingNewComment && isShapeDrawing
    : showForActiveObject || showForDocumentHighlight;

  const isEditorFocused = !isMdWidth || isMobileExpanded || hasAnnotations;

  useEffect(() => {
    if (!isMdWidth || !shouldShow) return;

    const shapeChanged = shapeRef.current !== shapesDrawingTools.shape;
    shapeRef.current = shapesDrawingTools.shape;

    if (shapeChanged && !hasAnnotations) {
      setIsMobileExpanded(false);
    }
  }, [isMdWidth, shouldShow, shapesDrawingTools.shape, hasAnnotations]);

  useEffect(() => {
    if (!shouldShow || !hasAnnotations) return;
    if (isMdWidth) {
      setIsMobileExpanded(true);
    }
    setIsCommentFieldFocused(true);
  }, [shouldShow, hasAnnotations, isMdWidth, setIsCommentFieldFocused]);

  if (!shouldShow) return null;

  const editorPosition = showForActiveObject
    ? position
    : positionFromDocumentHighlight;

  const showMobileHeader = isMdWidth && !!history?.length;

  return (
    <>
      {showMobileHeader && (
        <div className="float_comment_editor_head">
          <div className="float_comment_editor_head-btn">
            <Button
              htmlType="button"
              className="media_viewer_btn b-only-icon"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
            >
              <ArrowLeftSvg />
            </Button>
          </div>
          Add annotation
          <div className="float_comment_editor_head-actions">
            <Button
              htmlType="button"
              className="media_viewer_btn b-only-icon"
              disabled={!canUndo}
              onClick={(e) => {
                e.stopPropagation();
                undoHistory();
              }}
            >
              <BackSvg
                className={classNames(
                  'float_comment_editor_head-arrow',
                  canUndo && 'float_comment_editor_head-arrow_active'
                )}
              />
            </Button>
            <Button
              htmlType="button"
              className="media_viewer_btn b-only-icon"
              disabled={!canRedo}
              onClick={(e) => {
                e.stopPropagation();
                redoHistory();
              }}
            >
              <ForwardSvg
                className={classNames(
                  'float_comment_editor_head-arrow',
                  canRedo && 'float_comment_editor_head-arrow_active'
                )}
              />
            </Button>
          </div>
        </div>
      )}
      <div
        className="float_comment_editor"
        style={
          isMdWidth
            ? undefined
            : {
                left: editorPosition!.left,
                top: editorPosition!.top
              }
        }
      >
        <CommentEditor
          commentMentions={commentMentions}
          editCommentId="new"
          setEditCommentId={() => undefined}
          editorFor="new"
          autoFocus={hasAnnotations}
          isFocused={isEditorFocused}
          replyToCommentId={null}
          setReplyToCommentId={() => undefined}
          onSubmitEditor={onAddComment}
          isCheckboxChecked={isCheckboxChecked}
          setIsCheckboxChecked={setIsCheckboxChecked}
          checkboxValueType={checkboxValueType}
          potentialMentions={potentialMentions}
          potentialGroupMentions={potentialGroupMentions}
          searchPotentialMentions={(v) => setSearchQuery(v)}
          uppy={uppy}
          onRequestExpand={() => setIsMobileExpanded(true)}
        />
        {isEditorFocused && (
          <CommentTask
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            selectedBoard={selectedBoard}
            setSelectedBoard={setSelectedBoard}
            isShowTask={isShowTask}
            setIsShowTask={setIsShowTask}
            potentialAssignees={potentialAssignees}
            boardList={
              selectedVersion?.versionNumber === asset?.asset?.versionsCount
                ? taskBoards
                : []
            }
            canCreateTask={canCreateTask && user?.type === 'internal'}
            leftSlot={
              <div
                className="float_comment_editor_footer_cancel"
                onClick={onCancel}
              >
                Cancel
              </div>
            }
          />
        )}
      </div>
    </>
  );
}

export default FloatCommentEditor;
